import { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  X, Share2, ImageDown, Copy, Sprout, Trophy, Flame, TrendingUp, Star,
} from "lucide-react";
import { formatMoney } from "@/lib/currency";

/**
 * YearInReviewModal — opens a 1080×1920 vertical story-format PNG summarising
 * the user's year: net-worth headline, biggest saving month, streak peak,
 * completed-goals count + total-saved.
 *
 * Zero new backend endpoints — assembled client-side from data already
 * fetched by ShareHub (goals, streak, dashboard summary, cashflow report).
 */
export default function YearInReviewModal({
  open, onClose,
  summary, streak, goals, cashflow,   // preloaded by parent
  currency = "USD", userName,
}) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const insights = useMemo(() => computeYearInsights({ summary, streak, goals, cashflow }), [summary, streak, goals, cashflow]);

  if (!open) return null;

  const year = new Date().getFullYear();
  const firstName = (userName || "").split(" ")[0] || "";
  const shareText = t("year_modal.share_text", {
    year,
    name_suffix: firstName ? ` — ${firstName}` : "",
    goals_count: insights.goalsCompleted,
    goals_plural: insights.goalsCompleted !== 1 ? "s" : "",
    peak: insights.peakStreak,
    saved: formatMoney(insights.totalSaved, currency),
  });

  const generatePng = async () => {
    if (!cardRef.current) return null;
    setExporting(true);
    try {
      return await toPng(cardRef.current, {
        cacheBust: true, pixelRatio: 2,
        backgroundColor: "#12211A",
        skipFonts: true,
      });
    } catch (e) {
      console.error("Year-in-review PNG failed:", e);
      toast.error(t("goal_complete_modal.toast_create_image_failed"));
      return null;
    } finally { setExporting(false); }
  };

  const downloadPng = async () => {
    const url = await generatePng();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `pfo-year-${year}.png`;
    a.click();
    toast.success(t("year_modal.toast_story_saved"));
  };

  const shareImage = async () => {
    const url = await generatePng();
    if (!url) return;
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `pfo-year-${year}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: t("year_modal.share_title", { year }), text: shareText, files: [file] });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.warn("File share unsupported — falling back", e);
    }
    const a = document.createElement("a");
    a.href = url; a.download = `pfo-year-${year}.png`; a.click();
    try { await navigator.clipboard.writeText(shareText); } catch { /* ignore */ }
    toast.success(t("year_modal.toast_story_saved_caption"));
  };

  const copyOnly = async () => {
    try { await navigator.clipboard.writeText(shareText); toast.success(t("common.toast_copied")); }
    catch { toast.error(t("goal_complete_modal.toast_clipboard_blocked")); }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
        style={{ background: "rgba(15, 20, 25, 0.75)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
        data-testid="year-in-review-modal"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative max-w-md w-full rounded-2xl overflow-hidden shadow-lift animate-fadeup"
          style={{
            background: "linear-gradient(180deg, #FFFFFF 0%, #FBF5EB 100%)",
            border: "1px solid rgba(224,158,80,0.35)",
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-accent)] transition-colors"
            data-testid="year-in-review-close-btn"
            aria-label={t("year_modal.close")}
          >
            <X size={16} />
          </button>

          <div className="p-7 text-center">
            <div className="label-eyebrow" style={{ color: "#A16A25" }}>{t("year_modal.eyebrow")}</div>
            <h2
              className="text-2xl md:text-3xl font-medium tracking-tight leading-tight mt-2"
              style={{ fontFamily: "var(--font-heading)" }}
              data-testid="year-in-review-title"
            >
              {t("year_modal.title", { year })}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
              {t("year_modal.body")}
            </p>

            <div className="mt-5 grid grid-cols-2 gap-3 text-left">
              <MiniInsight label={t("year_modal.goals_completed")} value={insights.goalsCompleted} />
              <MiniInsight label={t("year_modal.streak_peak")} value={t("year_modal.streak_peak_val", { count: insights.peakStreak })} />
              <MiniInsight label={t("year_modal.best_month")} value={insights.bestMonth?.label || "—"} />
              <MiniInsight
                label={t("year_modal.total_saved")}
                value={formatMoney(insights.totalSaved, currency)}
              />
            </div>

            <div className="mt-6 flex items-center gap-2 justify-center flex-wrap">
              <button
                onClick={shareImage}
                className="btn-primary"
                disabled={exporting}
                data-testid="year-in-review-share-btn"
              >
                <Share2 size={14} />
                {exporting ? t("year_modal.preparing") : t("year_modal.share_story")}
              </button>
              <button
                onClick={downloadPng}
                className="btn-ghost"
                title={t("year_modal.save_png")}
                disabled={exporting}
                data-testid="year-in-review-download-btn"
              >
                <ImageDown size={14} />
              </button>
              <button
                onClick={copyOnly}
                className="btn-ghost"
                title={t("year_modal.copy_caption")}
                data-testid="year-in-review-copy-btn"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Off-screen 1080×1920 story card */}
      <div
        aria-hidden="true"
        style={{ position: "fixed", top: 0, left: "-99999px", pointerEvents: "none", opacity: 0 }}
      >
        <YearStoryCard
          ref={cardRef}
          year={year}
          firstName={firstName}
          summary={summary}
          insights={insights}
          currency={currency}
        />
      </div>
    </>,
    document.body,
  );
}

function MiniInsight({ label, value }) {
  return (
    <div
      className="rounded-lg p-3 border border-[var(--border-default)] bg-[var(--bg-accent)]"
      data-testid={`year-insight-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">{label}</div>
      <div className="font-mono text-lg font-medium mt-1 text-[var(--text-primary)]">{value}</div>
    </div>
  );
}

const YearStoryCard = forwardRef(function YearStoryCard(
  { year, firstName, summary, insights, currency }, ref,
) {
  const netWorth = Number(summary?.net_worth) || 0;
  const netWorthColor = netWorth >= 0 ? "#E09E50" : "#C88B6E";
  return (
    <div
      ref={ref}
      style={{
        width: 1080, height: 1920,
        background:
          "radial-gradient(ellipse 900px 700px at 25% 15%, rgba(224,158,80,0.36) 0%, transparent 55%)," +
          "radial-gradient(ellipse 900px 700px at 80% 90%, rgba(67,122,85,0.38) 0%, transparent 55%)," +
          "linear-gradient(180deg, #1A2E22 0%, #0F1B14 100%)",
        color: "#FDFCFB",
        fontFamily: "var(--font-body)",
        padding: "96px 80px",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        overflow: "hidden", position: "relative",
      }}
      data-testid="year-story-card"
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{
          width: 60, height: 60, borderRadius: 14, background: "#2D5A40",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Sprout size={32} color="#FDFCFB" strokeWidth={2.2} />
        </div>
        <div>
          <div style={{ fontSize: 15, letterSpacing: "0.32em", opacity: 0.7, textTransform: "uppercase" }}>Personal</div>
          <div style={{ fontFamily: "var(--font-heading)", fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em" }}>Finance Optimizer</div>
        </div>
      </div>

      {/* Headline block */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <div style={{
          fontSize: 26, letterSpacing: "0.36em", textTransform: "uppercase",
          color: "#E09E50", fontWeight: 500, marginBottom: 20,
        }}>
          Year in review
        </div>
        <div style={{
          fontFamily: "var(--font-heading)",
          fontSize: 132, fontWeight: 500, lineHeight: 0.92,
          letterSpacing: "-0.04em", marginBottom: 24,
        }}>
          {firstName ? `${firstName}'s ${year}` : year}
        </div>
        <div style={{
          fontSize: 40, opacity: 0.85, fontWeight: 400, lineHeight: 1.15,
          maxWidth: 900,
        }}>
          in money, made simple.
        </div>
      </div>

      {/* Big net worth */}
      <div>
        <div style={{
          fontSize: 20, letterSpacing: "0.32em", textTransform: "uppercase",
          color: "#E09E50", fontWeight: 500, marginBottom: 12,
        }}>
          Net worth at year&rsquo;s end
        </div>
        <div style={{
          fontFamily: "var(--font-mono)",
          fontSize: 152, fontWeight: 600, lineHeight: 1,
          letterSpacing: "-0.035em",
          color: netWorthColor,
        }}>
          {formatMoney(netWorth, currency)}
        </div>
      </div>

      {/* Stat grid — 2×2 */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 8,
      }}>
        <StoryStat
          icon={<Trophy size={26} color="#E09E50" />}
          label="Goals completed"
          value={String(insights.goalsCompleted)}
          detail={insights.goalsCompleted === 1 ? "milestone" : "milestones"}
          tint="#E09E50"
        />
        <StoryStat
          icon={<Flame size={26} color="#E09E50" />}
          label="Streak peak"
          value={`${insights.peakStreak}`}
          detail={insights.peakStreak === 1 ? "month" : "months"}
          tint="#E09E50"
        />
        <StoryStat
          icon={<TrendingUp size={26} color="#437A55" />}
          label="Best month"
          value={insights.bestMonth?.label || "—"}
          detail={insights.bestMonth
            ? `+${formatMoney(insights.bestMonth.cashflow, currency)}`
            : "keep saving"}
          tint="#437A55"
        />
        <StoryStat
          icon={<Star size={26} color="#F0C6A0" />}
          label="Total saved"
          value={formatMoney(insights.totalSaved, currency)}
          detail="across the year"
          tint="#F0C6A0"
        />
      </div>

      {/* Footer */}
      <div style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        paddingTop: 32, borderTop: "1px solid rgba(253,252,251,0.14)",
      }}>
        <div>
          <div style={{ fontSize: 15, letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.55, marginBottom: 6 }}>
            Building the habit
          </div>
          <div style={{ fontSize: 26, opacity: 0.9 }}>One year at a time.</div>
        </div>
        <div style={{
          fontFamily: "var(--font-mono)",
          fontSize: 18, letterSpacing: "0.08em",
          opacity: 0.55, textAlign: "right",
        }}>
          {year}
        </div>
      </div>
    </div>
  );
});

function StoryStat({ icon, label, value, detail, tint }) {
  return (
    <div style={{
      background: "rgba(253,252,251,0.06)",
      border: "1px solid rgba(253,252,251,0.10)",
      borderRadius: 20, padding: 28,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: "rgba(253,252,251,0.08)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {icon}
        </div>
        <div style={{ fontSize: 13, letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.6 }}>
          {label}
        </div>
      </div>
      <div style={{
        fontFamily: "var(--font-mono)",
        fontSize: 52, fontWeight: 600, lineHeight: 1,
        letterSpacing: "-0.025em",
        color: tint,
      }}>
        {value}
      </div>
      <div style={{ fontSize: 17, opacity: 0.65, marginTop: 10 }}>{detail}</div>
    </div>
  );
}

// ---- helpers ----
function computeYearInsights({ summary, streak, goals, cashflow }) {
  const goalsCompleted = (goals || []).filter(g => {
    const t = Number(g.target_amount) || 0;
    const s = Number(g.saved_amount) || 0;
    return t > 0 && s >= t;
  }).length;

  const peakStreak = Math.max(
    Number(streak?.longest_streak || 0),
    Number(streak?.current_streak || 0),
  );

  // Best month = the month in the last 12 with the highest positive cashflow
  const points = Array.isArray(cashflow?.points) ? cashflow.points : [];
  let bestMonth = null;
  let totalSaved = 0;
  for (const p of points) {
    const cf = Number(p.cashflow) || 0;
    if (cf > 0) totalSaved += cf;
    if (!bestMonth || cf > bestMonth.cashflow) {
      bestMonth = { label: p.month_label || p.month || "—", cashflow: cf };
    }
  }
  if (bestMonth && bestMonth.cashflow <= 0) bestMonth = null;

  // Fallback totalSaved from summary if cashflow report is unavailable
  if (totalSaved === 0 && summary?.cashflow_this_month > 0) {
    totalSaved = Number(summary.cashflow_this_month);
  }

  return { goalsCompleted, peakStreak, bestMonth, totalSaved };
}
